import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Spinner } from 'react-bootstrap';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import axios from '../api/axios';
import PropTypes from 'prop-types';

const OrdersKPI = ({ outlets, setOutletFilter, loadingOutlets, outletFilter }) => {
    const token = localStorage.getItem('authToken');
    const pieColors = ['#0088FE', '#FFBB28', '#FF8042'];
    const [lifetimeOrderData, setLifetimeOrderData] = useState({ 
        totalOrders: 0, 
        totalCancelledOrders: 0, 
        totalOrdersWithCoupons: 0, 
        totalOrdersWithoutCoupons: 0 
    });
    const [ordersData, setOrdersData] = useState({ 
        withCoupons: 0, 
        withoutCoupons: 0, 
        cancelled: 0,
        totalOrders: 0
    });
    const [dateFilter, setDateFilter] = useState('thisMonth');
    const [loadingLifetime, setLoadingLifetime] = useState(true);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const fetchLifetimeOrdersKIPData = useCallback(async () => {
        try {
            setLoadingLifetime(true);
            const response = await axios.get('dashboard/getOrdersStats', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response?.data?.status === true) {
                setLifetimeOrderData(response?.data?.data?.data);
            }
        } catch (error) {
            console.error('Error fetching lifetime orders data:', error);
        } finally {
            setLoadingLifetime(false);
        }
    }, [token]);

    const fetchOrdersKIPData = useCallback(async () => {
        try {
            setLoadingOrders(true);
            let url = 'dashboard/getOrdersStats';
            const queryParams = [];

            if (outletFilter !== 'all') {
                queryParams.push(`outletId=${outletFilter}`);
            }

            // Add dateFilter parameter instead of weekly/monthly
            queryParams.push(`dateFilter=${dateFilter}`);

            if (queryParams.length > 0) {
                url += `?${queryParams.join('&')}`;
            }

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.data?.status === true) {
                const {
                    totalOrdersWithCoupons,
                    totalOrdersWithoutCoupons,
                    totalCancelledOrders,
                    totalOrders
                } = response.data.data.data;

                setOrdersData({
                    withCoupons: totalOrdersWithCoupons || 0,
                    withoutCoupons: totalOrdersWithoutCoupons || 0,
                    cancelled: totalCancelledOrders || 0,
                    totalOrders: totalOrders || 0
                });
            }
        } catch (error) {
            console.error('Error fetching orders data:', error);
        } finally {
            setLoadingOrders(false);
        }
    }, [token, outletFilter, dateFilter]);

    // Initial load - fetch both lifetime and filtered data
    useEffect(() => {
        const fetchInitialData = async () => {
            await fetchLifetimeOrdersKIPData();
            await fetchOrdersKIPData();
            setInitialLoadComplete(true);
        };
        
        if (!initialLoadComplete) {
            fetchInitialData();
        }
    }, [fetchLifetimeOrdersKIPData, fetchOrdersKIPData, initialLoadComplete]);

    // Only fetch filtered data when outletFilter or dateFilter changes
    useEffect(() => {
        if (initialLoadComplete) {
            fetchOrdersKIPData();
        }
    }, [outletFilter, dateFilter, fetchOrdersKIPData, initialLoadComplete]);

    // Format date filter display name
    const getDateFilterDisplayName = (filter) => {
        const names = {
            'today': 'Today',
            'yesterday': 'Yesterday',
            'last7days': 'Last 7 Days',
            'last30days': 'Last 30 Days',
            'thisMonth': 'This Month',
            'lastMonth': 'Last Month'
        };
        return names[filter] || filter;
    };

    const pieData = [
        { 
            name: 'With Coupons', 
            value: ordersData.withCoupons, 
            percentage: ordersData.totalOrders > 0 ? 
                `${(ordersData.withCoupons / ordersData.totalOrders * 100).toFixed(2)}%` : '0%' 
        },
        { 
            name: 'Without Coupons', 
            value: ordersData.withoutCoupons, 
            percentage: ordersData.totalOrders > 0 ? 
                `${(ordersData.withoutCoupons / ordersData.totalOrders * 100).toFixed(2)}%` : '0%' 
        },
        { 
            name: 'Cancelled Orders', 
            value: ordersData.cancelled, 
            percentage: ordersData.totalOrders > 0 ? 
                `${(ordersData.cancelled / ordersData.totalOrders * 100).toFixed(2)}%` : '0%' 
        },
    ];

    const selectedOutlet = outlets.find((o) => o._id === outletFilter);
    const selectedOutletName = outletFilter === 'all' ? 'All Outlets' : (selectedOutlet?.name || '');

    return (
        <Container fluid className="py-4 shadow-sm" style={{ backgroundColor: 'white' }}>
            <h4 className='fw-bold'>ORDER ANALYTICS</h4>
            <hr />
            <Row>
                <Col md={4}>
                    <Row className="g-4">
                        {/* Total Orders Card */}
                        <Col md={12}>
                            <Card className="h-100 shadow-sm border-success">
                                <Card.Body className="d-flex justify-content-between">
                                    <div>
                                        <Card.Title className="text-muted mb-2">Total Orders</Card.Title>
                                        {loadingLifetime ? (
                                            <Spinner animation="border" variant="success" size="sm" />
                                        ) : (
                                            <Card.Text as="h4" className="text-success fw-bold">
                                                {lifetimeOrderData?.totalOrders}
                                            </Card.Text>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <i className="bi bi-cart-check fs-3 text-success"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Cancelled Orders Card */}
                        <Col md={12}>
                            <Card className="h-100 shadow-sm border-danger">
                                <Card.Body className="d-flex justify-content-between">
                                    <div>
                                        <Card.Title className="text-muted mb-2">Cancelled Orders</Card.Title>
                                        {loadingLifetime ? (
                                            <Spinner animation="border" variant="danger" size="sm" />
                                        ) : (
                                            <Card.Text as="h4" className="text-danger fw-semibold">
                                                {lifetimeOrderData?.totalCancelledOrders}
                                            </Card.Text>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <i className="bi bi-x-circle fs-3 text-danger"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Orders with Coupons Card */}
                        <Col md={12}>
                            <Card className="h-100 shadow-sm border-primary">
                                <Card.Body className="d-flex justify-content-between">
                                    <div>
                                        <Card.Title className="text-muted mb-2">Orders with Coupons</Card.Title>
                                        {loadingLifetime ? (
                                            <Spinner animation="border" variant="primary" size="sm" />
                                        ) : (
                                            <Card.Text as="h4" className="text-primary fw-semibold">
                                                {lifetimeOrderData?.totalOrdersWithCoupons}
                                            </Card.Text>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <i className="bi bi-ticket-perforated fs-3 text-primary"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Orders without Coupons Card */}
                        <Col md={12}>
                            <Card className="h-100 shadow-sm border-warning">
                                <Card.Body className="d-flex justify-content-between">
                                    <div>
                                        <Card.Title className="text-muted mb-2">Orders without Coupons</Card.Title>
                                        {loadingLifetime ? (
                                            <Spinner animation="border" variant="warning" size="sm" />
                                        ) : (
                                            <Card.Text as="h4" className="text-warning fw-semibold">
                                                {lifetimeOrderData?.totalOrdersWithoutCoupons}
                                            </Card.Text>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <i className="bi bi-ticket fs-3 text-warning"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>

                {/* RIGHT SIDE: Pie Chart & Filters */}
                <Col md={8}>
                    {/* Title */}
                    <h5 className="fw-semibold text-secondary">
                        Orders Distribution ({getDateFilterDisplayName(dateFilter)}) -{' '}
                        {loadingOutlets ? (
                            <Spinner animation="border" variant="secondary" size="sm" />
                        ) : (
                            selectedOutletName
                        )}
                    </h5>

                    {/* Filters Row */}
                    <Row className="mb-2 g-3 justify-content-end">
                        {/* Date Filter */}
                        <Col md="auto">
                            <div className="d-flex align-items-center bg-light p-2 rounded">
                                <i className="bi bi-calendar-event me-2 text-primary" />
                                <Form.Select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    style={{ width: '150px' }}
                                    className="border-0 bg-light"
                                >
                                    <option value="today">Today</option>
                                    <option value="yesterday">Yesterday</option>
                                    <option value="last7days">Last 7 Days</option>
                                    <option value="last30days">Last 30 Days</option>
                                    <option value="thisMonth">This Month</option>
                                    <option value="lastMonth">Last Month</option>
                                </Form.Select>
                            </div>
                        </Col>

                        {/* Outlet Filter */}
                        <Col md="auto">
                            <div className="d-flex align-items-center bg-light p-2 rounded">
                                <i className="bi bi-shop-window me-2 text-primary" />
                                {loadingOutlets ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <Form.Select
                                        value={outletFilter}
                                        onChange={(e) => setOutletFilter(e.target.value)}
                                        style={{ width: '150px' }}
                                        className="border-0 bg-light"
                                    >
                                        <option value="all">All Outlets</option>
                                        {outlets
                                            ?.filter((outlet) => outlet.status === "ACTIVE")
                                            ?.map((outlet) => (
                                                <option key={outlet?._id} value={outlet?._id}>
                                                    {outlet?.name}
                                                </option>
                                            ))}
                                    </Form.Select>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {/* Pie Chart */}
                    <div className="bg-light p-3 rounded shadow-sm">
                        {loadingOrders ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '250px' }}>
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart key={`${dateFilter}-${outletFilter}`}>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={90}
                                        dataKey="value"
                                        label={({ name, percentage }) => `${name}: ${percentage}`}
                                        animationDuration={1000}
                                        animationEasing="ease-out"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={pieColors[index % pieColors.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        formatter={(value, name, props) => [
                                            value, 
                                            `${name}: ${props.payload.percentage}`
                                        ]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Legend */}
                    <div className="d-flex justify-content-around mt-4">
                        {loadingOrders ? (
                            <Spinner animation="border" variant="secondary" />
                        ) : (
                            pieData.map((entry, index) => (
                                <div key={index} className="text-center">
                                    <div
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            backgroundColor: pieColors[index % pieColors?.length],
                                            display: 'inline-block',
                                            marginRight: '8px',
                                            borderRadius: '4px',
                                        }}
                                    />
                                    <span style={{ fontWeight: 'bold' }}>{entry.name}: </span>
                                    <span>{entry.value} ({entry.percentage})</span>
                                </div>
                            ))
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

OrdersKPI.propTypes = {
    outlets: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired
    })).isRequired,
    setOutletFilter: PropTypes.func.isRequired,
    loadingOutlets: PropTypes.bool.isRequired,
    outletFilter: PropTypes.string.isRequired
};

export default OrdersKPI;